
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import FormAssemblyFormsList from '@/components/FormAssemblyFormsList';
import JotformFormsList from '@/components/JotformFormsList';
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useProvider, type ActiveProvider } from "@/contexts/ProviderContext";
import { useAutomation } from "@/hooks/useAutomation";
import * as Sentry from "@sentry/react";
import { logger } from '@/lib/logger';

const DashboardPage = () => {
  const [searchParams] = useSearchParams();
  const showAllProviders = searchParams.get("provider") === "all";
  const { activeProvider, setActiveProvider, connectionStatus } = useProvider();
  useAutomation();

  // Sentry error test button - only show in development
  const isDevelopment = import.meta.env.DEV;

  const handleSentryTest = () => {
    // Debug: Check if Sentry is loaded
    logger.debug('Sentry DSN from env:', import.meta.env.VITE_SENTRY_DSN);
    logger.debug('Is Sentry available?:', typeof Sentry.captureException === 'function');
    logger.debug('Sentry object:', Sentry);

    // Method 1: Direct Sentry.captureException
    const testError = new Error('Sentry test error: This is your first error!');
    logger.debug('Sending error to Sentry via captureException...');
    const eventId = Sentry.captureException(testError);
    logger.debug('Sentry Event ID:', eventId);

    // Method 2: Also throw it to be caught by error boundary
    setTimeout(() => {
      throw testError;
    }, 100);
  };

  const ConnectionDot = ({ provider }: { provider: ActiveProvider }) => {
    const status = connectionStatus[provider];
    if (status === true) {
      return <span className="inline-block w-2 h-2 rounded-full bg-green-500 ml-1.5" />;
    }
    return null;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow">
        {/* Container for forms list */}
        <div className="container mx-auto px-4 py-8">
          {/* Sentry Test Button - Development Only */}
          {isDevelopment && (
            <div className="mb-6 p-4 border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">
                  Development Tools - Sentry Testing
                </h3>
              </div>
              <div className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                <p className="mb-2">Test Sentry error tracking integration</p>
                <p className="font-mono text-xs">
                  DSN: {import.meta.env.VITE_SENTRY_DSN ?
                    `${import.meta.env.VITE_SENTRY_DSN.substring(0, 30)}...` :
                    'Not loaded'}
                </p>
                <p className="font-mono text-xs">
                  Sentry initialized: {import.meta.env.VITE_SENTRY_DSN ? 'Yes' : 'No'}
                </p>
              </div>
              <Button
                onClick={handleSentryTest}
                variant="destructive"
                size="sm"
                className="gap-2"
              >
                <AlertCircle className="h-4 w-4" />
                Trigger Sentry Test Error
              </Button>
            </div>
          )}

          {/* Provider Tabs — only visible with ?provider=all */}
          {showAllProviders ? (
            <>
              <Tabs
                value={activeProvider}
                onValueChange={(value) => setActiveProvider(value as ActiveProvider)}
                className="mb-2"
              >
                <TabsList>
                  <TabsTrigger value="formassembly" className="gap-1">
                    FormAssembly
                    <ConnectionDot provider="formassembly" />
                  </TabsTrigger>
                  <TabsTrigger value="jotform" className="gap-1">
                    Jotform
                    <ConnectionDot provider="jotform" />
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {activeProvider === 'formassembly' && <FormAssemblyFormsList />}
              {activeProvider === 'jotform' && <JotformFormsList />}
            </>
          ) : (
            <JotformFormsList />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DashboardPage;
